import React, { useMemo, useEffect } from 'react'
import tw, { styled } from 'twin.macro'
import { useAtomValue, useSetAtom } from 'jotai'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'

import {useNamedBoundingRectRef, useProgressControl} from '@/components/Tour'
import { mcpEndpointPrefixAtom } from '@/features/spirit/atoms/hosts'
import { currentAccountAtom } from '@/features/identity/atoms'
import { queryMcpLinkedData } from '@/features/spirit/queries'

import { injectModalVisibleAtom } from './atoms/injectModalVisibleAtom'
import InjectModal from './components/InjectModal'
import { claimSpiritStageAtom, claimSpiritStageAnimateAtom, ClaimSpiritStageEnum } from './atoms/claimSpiritStageAtom'

export const Wrapper = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 58vw;
  cursor: pointer;
`

export const TextOne = styled.div`
  ${tw`font-secondary font-medium`}
  font-size: 0.833vw;
  color: #27ffe2;
  margin-bottom: 1.21vh;
`

export const TextTwo = styled.div`
  ${tw`font-primary`}
  font-size: 0.73vw;
  color: #b5b5b5;
  margin-bottom: 1.01vh;
`

export const LineImg = styled.img`
  width: 14.11vw;
  height: auto;
  position: relative;
  left: -14%;
`

export const PercentageBased = styled.div`
  ${tw`font-secondary font-medium`}
  font-size: 0.833vw;
  color: #27ffe2;
  position: relative;
  left: 50%;
  top: -3vh;
`

export const usePlatformPercentageValue = (): string => {
  const mcpPrefix = useAtomValue(mcpEndpointPrefixAtom)
  const account = useAtomValue(currentAccountAtom)
  const {data} = useQuery(queryMcpLinkedData(mcpPrefix, account?.address || ''))

  return useMemo(() => {
    if (data) {
      const injectedCount = Object.keys(data).length
      // fix me: totalCount calculate, bsc and evm are the same key
      const totalCount = 6
      return `${Math.floor((injectedCount / totalCount) * 100)}%`
    }

    return '0%'
  }, [data])
}

const Percentage: React.FC = () => {
  const platformPercentage = usePlatformPercentageValue()
  return <PercentageBased>{platformPercentage}</PercentageBased>
}

export default function InjectSection() {
  const stage = useAtomValue(claimSpiritStageAtom)
  const variants = useAtomValue(claimSpiritStageAnimateAtom)
  const setIsOpenInjectModal = useSetAtom(injectModalVisibleAtom)

  const ref = useNamedBoundingRectRef<HTMLDivElement>(
    'generateIdentityFirstHint'
  )
  const dispatch = useProgressControl('generateIdentityFirstHint')
  useEffect(() => {
    const seed = setTimeout(() => {
      dispatch('next')
    }, 4500)
    return () => {
      clearTimeout(seed)
      dispatch('cancel')
    }
  }, [dispatch])

  const [headline, subline] = (stage === ClaimSpiritStageEnum.claimed || stage === ClaimSpiritStageEnum.injected)
    ? [
      'Click to enrich your data',
      'Inject More Data to Your Spirit to Achieve Higher Level!',
    ]
    : [
      'Click to generate identity first',
      'The More Data Injected, The Higher Level Your Spirit Will Be',
    ]
  return (
    <>
      <AnimatePresence mode="wait">
        <Wrapper
          onClick={() => {
            dispatch('cancel')
            setIsOpenInjectModal(true)
          }}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={variants.injectSection}
        >
          <div css={[tw`relative`, 'top:-12vw;']}>
            <TextOne>{headline}</TextOne>
            <TextTwo>{subline}</TextTwo>
            <LineImg
              src="/static/images/indexPage/Spirit/Line.png"
              alt="shell-line"
            />
            {stage !== ClaimSpiritStageEnum.original && <Percentage />}
          </div>
        </Wrapper>
      </AnimatePresence>
      <InjectModal />
    </>
  )
}
